import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { db } from '@/firebase/config';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, DollarSign } from "lucide-react";
import { calculateFeeBalances, type PaymentAllocations, getApplicableTermFees, Term } from '@/utils/feeCalculations';



/**
 * SchoolFinanceLogic
 * 
 * This component (and the school owner's financial dashboard in general) shares the 
 * EXACT SAME calculation logic as the Guardian Finance view (`GuardianFinance.tsx`).
 * 
 * The core "brain" is the `calculateFeeBalances` utility function imported above.
 * This ensures that:
 * 1. "Payment is Payment": The school sees the exact same aggregated totals as the parent.
 * 2. Carryovers (Credits & Debts): The flow from Term 1 -> Term 2 -> Term 3 is identical.
 * 3. Historical Debt: Brought forward balances are handled the same way.
 * 
 * Any additional logic for the School Owner (e.g., aggregate revenue, arrears analysis)
 * will be built ON TOP of this foundational logic.
 */

// Export the hook for use in other components (e.g. FinanceDashboard)
export const useSchoolFinanceData = () => {
  const user = useSelector((state: any) => state.users?.currentUser || {});
  const schoolID = user.schoolID || user.selectedAffiliation?.schoolID;

  const [totalBalance, setTotalBalance] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [paymentProviders, setPaymentProviders] = useState<string[]>(['mpesa', 'equity', 'kcb', 'cash', 'card', 'bank']);

  const [outstandingByClass, setOutstandingByClass] = useState<{name: string, amount: number, students: number, missingData: number}[]>([]);
  const [calculatingClasses, setCalculatingClasses] = useState<boolean>(true);
  const [validationStats, setValidationStats] = useState({ missingFeeStructure: false, studentsWithMissingDates: 0 });

  // 1. Fetch enabled payment methods
  useEffect(() => {
    if (!schoolID) return;
    const fetchPaymentMethods = async () => {
      try {
        const methodsRef = collection(db, `schools/${schoolID}/systemConfig/paymentMethods/list`);
        const snap = await getDocs(methodsRef);
        const params: string[] = [];
        
        if (!snap.empty) {
          snap.forEach(doc => {
            if (doc.data().enabled) params.push(doc.id);
          });
        }
        
        if (params.length === 0) {
          const configDoc = await getDoc(doc(db, `schools/${schoolID}/systemConfig/paymentMethods`));
          if (configDoc.exists()) {
             const data = configDoc.data();
             const methods = data.methods || [];
             methods.forEach((m: any) => {
               if (typeof m === 'string') params.push(m);
               else if (m.enabled && m.id) params.push(m.id);
             });
          }
        }

        if (params.length > 0) {
          if (!params.includes('bank')) params.push('bank');
          setPaymentProviders(Array.from(new Set(params)));
        }
      } catch (e) {
        console.error("Failed to load payment methods", e);
      }
    };
    fetchPaymentMethods();
  }, [schoolID]);

  // 2. Aggregate Total Balance
  useEffect(() => {
    if (!schoolID) return;
    
    const calculateTotalBalance = async () => {
      setLoading(true);
      let grandTotal = 0;

      try {
        const promises = paymentProviders.map(async (provider) => {
          const q = query(
            collection(db, `schools/${schoolID}/transactions/${provider}/records`),
            where("status", "==", "completed")
          );
          const snap = await getDocs(q);
          let providerTotal = 0;
          snap.forEach(doc => {
            const data = doc.data();
            if (data.amount) providerTotal += Number(data.amount);
          });
          return providerTotal;
        });

        const results = await Promise.all(promises);
        grandTotal = results.reduce((acc, curr) => acc + curr, 0);
        
        setTotalBalance(grandTotal);
      } catch (error) {
        console.error("Error calculating total school balance:", error);
      } finally {
        setLoading(false);
      }
    };

    calculateTotalBalance();
  }, [schoolID, paymentProviders]);

  // 3. Calculate Outstanding Fees by Class
  useEffect(() => {
    if (!schoolID || paymentProviders.length === 0) return;

    const calculateClassBalances = async () => {
      setCalculatingClasses(true);
      setValidationStats(prev => ({ ...prev, missingFeeStructure: false, studentsWithMissingDates: 0 }));

      try {
        const feeDocRef = doc(db, `schools/${schoolID}/deployedFeeStructures/current`);
        const termsRef = collection(db, `schools/${schoolID}/terms`);

        const [feeDoc, termsSnap] = await Promise.all([
            getDoc(feeDocRef),
            getDocs(termsRef)
        ]);

        const feeData = feeDoc.exists() ? feeDoc.data() : null;
        const terms = termsSnap.docs.map(t => ({ id: t.id, ...t.data() } as Term));
        
        if (!feeDoc.exists()) {
           setValidationStats(prev => ({ ...prev, missingFeeStructure: true }));
        }

        const levelFees = feeData?.levelFees || [];
        const studentsRef = collection(db, `schools/${schoolID}/students`);
        const studentsSnap = await getDocs(studentsRef);
        const students = studentsSnap.docs.map(d => d.data());

        const paymentsMap: Record<string, PaymentAllocations> = {};
        
        const providerPromises = paymentProviders.map(async (provider) => {
          const q = query(
            collection(db, `schools/${schoolID}/transactions/${provider}/records`),
            where("status", "==", "completed")
          );
          const snap = await getDocs(q);
          snap.forEach(doc => {
            const data = doc.data();
            const ref = data.accountReference;
            if (!ref) return;
            if (!paymentsMap[ref]) paymentsMap[ref] = { term1: 0, term2: 0, term3: 0 };
            const amt = Number(data.amount) || 0;
            const tKey = data.termKey || data.paymentForTerm || 'term1'; 
            if (tKey === 'term1') paymentsMap[ref].term1 += amt;
            else if (tKey === 'term2') paymentsMap[ref].term2 += amt;
            else if (tKey === 'term3') paymentsMap[ref].term3 += amt;
            else paymentsMap[ref].term1 += amt;
          });
        });
        await Promise.all(providerPromises);

        const classAggregation: Record<string, { amount: number, studentCount: number, missingDataCount: number }> = {};
        let totalMissingDates = 0;

        students.forEach(student => {
          const className = student.className || student.class || "Unknown Class";
          const regNo = student.registrationNumber;
          const studentRef = `Fee-${regNo}`;
          const grade = student.admissionGrade || student.grade;
          
          if (!classAggregation[className]) {
            classAggregation[className] = { amount: 0, studentCount: 0, missingDataCount: 0 };
          }
          
          // ALWAYS count the student
          classAggregation[className].studentCount += 1;
          
          if (!student.admissionDate) {
             totalMissingDates++;
             classAggregation[className].missingDataCount += 1;
          }

          const feesObj = levelFees.find((l: any) => l.grade === grade) || {};
          
          // For ongoing students, skip admission date logic - they owe all terms
          const isOngoing = !!student.ongoing;
          const totals = getApplicableTermFees(student.admissionDate, feesObj, terms, isOngoing);

          const paid: PaymentAllocations = paymentsMap[studentRef] || { term1: 0, term2: 0, term3: 0 };
          
          if (student.isGovernmentSponsored) {
             const sponsorship = Number(student.governmentSponsorshipAmount || 0);
             if (totals.term1 > 0) totals.term1 = Math.max(0, totals.term1 - (sponsorship/3));
             if (totals.term2 > 0) totals.term2 = Math.max(0, totals.term2 - (sponsorship/3));
             if (totals.term3 > 0) totals.term3 = Math.max(0, totals.term3 - (sponsorship/3));
          }

          const broughtForward = Number(student.outstandingBalance || 0);
          const result = calculateFeeBalances(totals, paid, broughtForward);
          const debt = result.totalOutstanding;

          if (debt > 0) {
            classAggregation[className].amount += debt;
          }
        });

        setValidationStats(prev => ({ ...prev, studentsWithMissingDates: totalMissingDates }));

        const sorted = Object.entries(classAggregation)
          .map(([name, data]) => ({ 
            name, 
            amount: data.amount, 
            students: data.studentCount,
            missingData: data.missingDataCount 
          }))
          .sort((a, b) => {
             // Sort by Debt (High to Low), then by Missing Data, then by Name
             if (b.amount !== a.amount) return b.amount - a.amount;
             if (b.missingData !== a.missingData) return b.missingData - a.missingData;
             return a.name.localeCompare(b.name);
          });

        setOutstandingByClass(sorted);

      } catch (err) {
        console.error("Error calculating class balances:", err);
      } finally {
        setCalculatingClasses(false);
      }
    };

    calculateClassBalances();
  }, [schoolID, paymentProviders]);

  return {
    totalBalance,
    loading,
    outstandingByClass,
    calculatingClasses,
    paymentProviders,
    schoolID,
    validationStats
  };
};

const SchoolFinanceLogic = () => {
  const { totalBalance, loading, outstandingByClass, calculatingClasses, paymentProviders, schoolID } = useSchoolFinanceData();

  if (!schoolID) {
    return <div className="p-4">Please select a school to view finances.</div>;
  }

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl font-bold mb-4">School Finance Logic</h1>
      
      {/* Top Row: Total Balance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-900 to-slate-900 text-white border-none shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-medium opacity-90">
              Total School Balance
            </CardTitle>
            <DollarSign className="h-5 w-5 opacity-70" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 text-2xl font-bold opacity-50">
                <Loader2 className="h-6 w-6 animate-spin" />
                Calculating...
              </div>
            ) : (
              <div className="space-y-1">
                <div className="text-4xl font-bold">
                  KES {totalBalance.toLocaleString()}
                </div>
                <p className="text-xs opacity-70">
                  Aggregated from {paymentProviders.length} payment providers
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Outstanding Fees by Class */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-orange-600" />
            Outstanding Fees by Class
          </CardTitle>
        </CardHeader>
        <CardContent>
          {calculatingClasses ? (
             <div className="flex items-center justify-center py-8 text-muted-foreground">
               <Loader2 className="h-6 w-6 animate-spin mr-2" />
               Analyzing student balances...
             </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {outstandingByClass.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg bg-card hover:bg-accent/50 transition-colors">
                  <div>
                    <div className="font-bold text-lg">{item.name}</div>
                    <div className="text-sm text-muted-foreground">{item.students} students owing</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-orange-600">KES {item.amount.toLocaleString()}</div>
                    <span className="text-xs text-orange-600/80 font-medium bg-orange-100 dark:bg-orange-900/30 px-2 py-0.5 rounded">
                      Overdue
                    </span>
                  </div>
                </div>
              ))}
              {outstandingByClass.length === 0 && (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  No outstanding fees found.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-8 text-sm text-gray-500">
        <p><strong>Calculation Logic:</strong></p>
        <ul className="list-disc ml-5 space-y-1 mt-2">
          <li><strong>Total Balance:</strong> Sum of all completed payments across registered providers.</li>
          <li><strong>Outstanding by Class:</strong> 
            Aggregates individual student debts using the core fee algorithm 
            (Fees + Historical Debt - Payments - Sponsorships).
          </li>
        </ul>
      </div>
    </div>
  );
};


export default SchoolFinanceLogic;
